import { useCallback, useEffect, useState } from 'react';

// Solo un menú desplegable puede estar abierto a la vez en toda la app: guardamos aquí
// cómo cerrar el que está abierto para cerrarlo cuando se abra otro.
let closeActiveMenu: (() => void) | undefined;

const releaseActiveMenu = (close: () => void) => {
  if (closeActiveMenu === close) {
    closeActiveMenu = undefined;
  }
};

// isInside indica si un elemento pertenece al menú (control o lista); un clic fuera
// de él, o Esc, lo cierra aunque el foco no haya salido del control.
export const useExclusiveMenu = (isInside: (target: Node) => boolean) => {
  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => {
    releaseActiveMenu(close);
    setIsOpen(false);
  }, []);

  const open = useCallback(() => {
    if (closeActiveMenu !== close) {
      closeActiveMenu?.();
    }
    closeActiveMenu = close;
    setIsOpen(true);
  }, [close]);

  useEffect(() => () => releaseActiveMenu(close), [close]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Node) || !isInside(event.target)) {
        close();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    // En captura: el menú de react-select detiene la propagación de sus eventos.
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isInside, close]);

  return { isOpen, open, close };
};
